import React from "react";
import type { NextPage } from "next";
import { Page } from "components/page";
import { sectionSpacingY } from "components/section";
import {
  ReverseEtlHero,
  GovernanceAndSecurity,
  DeveloperFriendly,
  WarehousePerformance,
} from "components/platform/reverse-etl";
import { StackedLogos } from "components/logos";
import { DestinationMarquee } from "components/content";
import { DataTeamsCards } from "components/platform-overview-content";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { AuraPlate } from "ui/plates";
import { CenteredQuote } from "components/quotes";
import { integrationsCount } from "../../constants/integrations";

const ReverseETL: NextPage<LandingPageProps> = ({
  page,
  destinations,
  announcementBanner,
  defaultLogos,
  defaultMobileLogos,
  customers,
}) => {
  const prestaShop = getSafeCustomer(customers, "prestashop");

  return (
    <Page
      hasSmoothScroll
      theme="dark"
      bgColor="new.green.950"
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <ReverseEtlHero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      <StackedLogos
        customers={defaultLogos}
        mobileCustomers={defaultMobileLogos}
        title="#1 in Reverse ETL and trusted by leading data teams"
        titleMaxW="100%"
        my={0}
        py={sectionSpacingY}
      />

      <WarehousePerformance />

      <AuraPlate condensed variant="blue" my={sectionSpacingY}>
        <CenteredQuote
          quoteText="Data is now accessible to every stakeholder within the company regardless of their technical abilities."
          author={{ name: "Remi Paulin", job: "Data Architect" }}
          company={{ name: "PrestaShop", logo: prestaShop.logoUrl }}
          cta={{ text: "Read the story", href: "/customers/prestashop" }}
          py={{ base: 0, md: "smaller" }}
        />
      </AuraPlate>

      <DataTeamsCards hasHorizontalCard my={0} pb={sectionSpacingY} />

      <GovernanceAndSecurity />

      <DeveloperFriendly />

      <DestinationMarquee
        destinations={destinations}
        title={`${integrationsCount}+ fully-managed connectors`}
        description="Hightouch helps your data warehouse become the hub that keeps everyone connected across marketing, sales, finance, and more."
        textMaxW="800px"
        my={0}
        pb={sectionSpacingY}
      />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/platform/reverse-etl", {
  loadDefaultLogos: true,
  loadDefaultMobileLogos: true,
  loadDestinations: "all",
  loadCustomers: ["prestashop"],
});

export default ReverseETL;
